import { FieldValue, isEmpty } from "../../../Schema";

/**
 * Transforms for the fields that hold a whole number in a column that is not one.
 *
 * `instrumentGTIN` and `maintenanceIntervalInDays` are numeric to every consumer — the grid
 * filters them as numbers, a CSV cell holds digits, and validation rejects anything else —
 * but they are not stored alike. `instrumentGTIN` is a `String` in GraphQL and `S` in the
 * table; `maintenanceIntervalInDays` is an `Int` and an `N`. Declaring `data.type: NUMBER`
 * alone bridges neither: DataManager passes the value straight through, so the field would
 * claim to be a number and hold `"90"`. These carry it across the boundary instead, which
 * is what lets the type be honest and lets a consumer stop keeping its own list of which
 * fields to check.
 *
 * Which of the two writers a field wants therefore depends on its column, not on the fact
 * that it is numeric — see {@link toNumericText} and {@link toNumericValue}.
 */

/** Digits only, no leading zero. The rule the form and the grid already enforce. */
const WHOLE_NUMBER = /^[1-9][0-9]*$/;

/**
 * Reads a CSV cell as a number.
 *
 * Text that is not a whole number is passed through untouched rather than dropped, so the
 * field's own validation reports it against the value the file actually contained.
 *
 * @param value - The cell value.
 * @returns The number, `EMPTY_VALUE` for an empty cell, or the original text.
 */
export const fromNumericCell = (value: FieldValue): FieldValue => {
  if (isEmpty(value)) {
    return value;
  }
  const text = String(value).trim();
  return WHOLE_NUMBER.test(text) ? Number(text) : value;
};

/**
 * Writes the value back as text, for a column that holds one.
 *
 * @param value - The field value.
 * @returns The text, or `EMPTY_VALUE` when there is no value.
 */
export const toNumericText = (value: FieldValue): FieldValue =>
  isEmpty(value) ? value : String(value);

/**
 * Writes the value back as a number, for a column that holds one.
 *
 * Text that is not a whole number is passed through rather than becoming `NaN`, so the
 * field's validation reports it instead of the write silently corrupting it.
 *
 * @param value - The field value.
 * @returns The number, `EMPTY_VALUE` when there is no value, or the original text.
 */
export const toNumericValue = (value: FieldValue): FieldValue => {
  if (isEmpty(value)) {
    return value;
  }
  const text = String(value).trim();
  return WHOLE_NUMBER.test(text) ? Number(text) : value;
};
